﻿import { DataGrid } from "@serenity-is/corelib";
import { isNonDataRow } from "./GridRowFlags";

/**
 * Export and Print for every list page — the two actions the prototype puts in the grid
 * header beside Columns and Refresh (eFMS Prototype.dc.html, FRANCHISEES GRID).
 *
 * ⚠ IN THE PROTOTYPE NEITHER IS WIRED: both raise a toast reading "… is not wired in this
 * prototype". This project does not ship dead controls — the same rule that kept a
 * non-functioning *Keep me signed in* off the sign-in screen and *Email owners* out of the
 * bulk bar — so both do the real thing here.
 *
 * WHAT "REAL" MEANS FOR EACH
 * Export writes a CSV of what the user is actually looking at: the visible columns, in
 * their current order, for the rows the current search, filters and sort have produced.
 * Not the whole table — a filtered grid that exports everything is a bug, not a feature.
 *
 * ⚠ Print cannot be a stylesheet alone. SleekGrid is VIRTUALIZED: only the rows in view
 * exist in the DOM, so `window.print()` over the real grid prints the twenty-odd visible
 * rows and silently loses the rest — and column widths are inline pixel values sized for a
 * viewport, not a page. So Print builds a plain table from the same data as the export,
 * prints that, and removes it again. The `@media print` block in theme-core.css hides the app
 * and shows only that sheet.
 */

/**
 * Serenity/SleekGrid internal columns: the select checkbox, the expand chevron, the row
 * actions. Exported because the column picker has to make the same distinction — one
 * definition of "a real column", used by both, so they cannot disagree.
 */
export function isDataColumn(col: any): boolean {
    const field = col?.field as string | undefined;
    if (!field || field.startsWith("__")) return false;
    // The select column carries a checkbox glyph as its name, not a label.
    const name = typeof col.name === "string" ? col.name.replace(/<[^>]*>/g, "").trim() : "";
    return !!name && name !== "[x]";
}

/** A column's heading as plain text — shared, so a card, a band, a CSV and a print sheet
 * cannot label the same column three different ways. */
export function columnLabel(col: any): string {
    return String(col.name ?? col.field ?? "").replace(/<[^>]*>/g, "").trim();
}

const scratch = document.createElement("div");

/**
 * The text of whatever a column format callback handed back.
 *
 * ⚠ A `FormatterResult` is not a string. It may be a Node, an HTML string, or null, and every
 * caller that reads one has to reduce all three the same way or two views of the same value
 * disagree — which is why this is shared rather than inlined at each site. Returns null for
 * "the formatter produced nothing", which a caller distinguishes from the empty string it
 * deliberately produced.
 */
export function formatResultText(out: any): string | null {
    if (out instanceof Node)
        return (out.textContent || "").trim();
    if (out === "")
        return "";
    if (out == null)
        return null;
    scratch.innerHTML = String(out);
    return (scratch.textContent || "").trim();
}

/**
 * The minimal context a column format callback needs, standing in for the one SleekGrid builds
 * while it is painting a cell.
 *
 * ⚠ `escape` is not optional padding. corelib's own group formatter calls `ctx.escape(...)` on
 * the path where a grouping declares no format of its own, so a context without it throws for
 * exactly the grids that customised nothing.
 */
export function formatContext(grid: any, item: any, col: any, row: number, cell: number, fallback?: any) {
    return {
        item, value: fallback, column: col, grid, row, cell,
        escape: (v: any) => (v == null ? (fallback == null ? "" : String(fallback)) : String(v)),
        addClass: () => { }, addAttrs: () => { }, tooltip: () => { },
    };
}

/**
 * The text a cell shows, for one item and one column.
 *
 * ⚠ Exported for the same reason isDataColumn is: the card view has to say exactly what the
 * table cell says, and two implementations of "what does this cell read as" would drift on the
 * first enum or date column somebody adds.
 *
 * ⚠ Reading `item[field]` alone is wrong for half the columns in this app: list responses
 * carry an enum as its NUMERIC value (Status would export as "3" where the grid shows
 * "Draft"), dates arrive as ISO strings, and the name column is an EditLink. So the
 * column's own formatter is asked first, with a minimal context standing in for the one
 * SleekGrid builds while rendering. A formatter may return a Node or an HTML string —
 * both are reduced to text — and anything that throws falls back to the raw value rather
 * than failing the export.
 */
export function cellText(grid: any, item: any, col: any, row: number, cell: number): string {
    const raw = col.field ? item?.[col.field] : undefined;

    if (typeof col.format === "function") {
        try {
            const text = formatResultText(col.format(formatContext(grid, item, col, row, cell, raw)));
            if (text != null) return text;
        } catch { /* fall through to the raw value */ }
    }

    if (raw == null) return "";
    if (raw instanceof Date) return raw.toLocaleDateString();
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}(T|$)/.test(raw)) {
        const d = new Date(raw);
        if (!isNaN(+d)) return d.toLocaleDateString();
    }
    return String(raw);
}

export interface GridSnapshot {
    title: string;
    headers: string[];
    rows: string[][];
}

/**
 * Everything the user can currently see, as text.
 *
 * ⚠ Walks `getLength()` / `getItem(i)` — the DataView interface the GRID ITSELF renders
 * from — rather than `getItems()`. That is what makes the export follow the live view:
 * quick search and quick filters are applied server-side by Serenity's RemoteView, sorting
 * and paging reorder what it hands back, and `getItem(i)` is the only reading that is
 * guaranteed to be the sequence on screen. (Verified live: with a search that matches
 * nothing, this correctly produces a header and no rows.)
 *
 * Non-data rows are dropped — the franchisee row-expand inserts spacer rows to reserve its
 * panel's height, and they would otherwise export as blank lines.
 */
export function snapshot(grid: DataGrid<any>): GridSnapshot {
    const slick = grid.slickGrid as any;
    const columns = (slick?.getColumns?.() || []).filter(isDataColumn);
    const view = grid.view as any;

    const items: any[] = [];
    const count = view?.getLength?.() ?? 0;
    for (let i = 0; i < count; i++) {
        const item = view.getItem(i);
        if (item && !isNonDataRow(item)) items.push(item);
    }

    const rows = items.map((item, r) =>
        columns.map((col: any, c: number) => cellText(slick, item, col, r, c)));

    const title = (grid.domNode?.parentElement?.querySelector(".m-list-heading .grid-title")?.textContent
        || document.title || "Export").trim();

    return { title, headers: columns.map(columnLabel), rows };
}

/** RFC 4180 quoting: double the quotes, wrap anything containing a delimiter or newline. */
function csvCell(value: string): string {
    return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function fileStamp(): string {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function exportCsv(grid: DataGrid<any>) {
    const snap = snapshot(grid);
    const lines = [snap.headers, ...snap.rows].map(r => r.map(csvCell).join(","));

    // ⚠ CRLF and a UTF-8 BOM, both for Excel: without the BOM it reads the file as the
    // system codepage and mangles every non-ASCII name (Coimbatore is fine, "Ελλάδα" is not).
    const blob = new Blob(["﻿" + lines.join("\r\n") + "\r\n"],
        { type: "text/csv;charset=utf-8" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${snap.title.replace(/[^\w\-. ]+/g, "_")} ${fileStamp()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoked on a timer rather than immediately: Chrome needs the URL to survive the
    // click long enough to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);

    return snap.rows.length;
}

export function printGrid(grid: DataGrid<any>) {
    const snap = snapshot(grid);

    document.querySelector(".m-print-sheet")?.remove();

    const sheet = document.createElement("section");
    sheet.className = "m-print-sheet";

    const head = document.createElement("header");
    head.className = "m-print-head";
    const h1 = document.createElement("h1");
    h1.textContent = snap.title;
    const meta = document.createElement("p");
    meta.textContent = `${snap.rows.length} ${snap.rows.length === 1 ? "record" : "records"} · ${new Date().toLocaleString()}`;
    head.append(h1, meta);

    const table = document.createElement("table");
    const thead = document.createElement("thead");
    const hr = document.createElement("tr");
    snap.headers.forEach(h => {
        const th = document.createElement("th");
        th.textContent = h;
        hr.appendChild(th);
    });
    thead.appendChild(hr);

    const tbody = document.createElement("tbody");
    snap.rows.forEach(r => {
        const tr = document.createElement("tr");
        r.forEach(v => {
            const td = document.createElement("td");
            td.textContent = v;
            tr.appendChild(td);
        });
        tbody.appendChild(tr);
    });

    table.append(thead, tbody);
    sheet.append(head, table);
    document.body.appendChild(sheet);

    // The sheet must be in the DOM and laid out before the print dialog opens, and the
    // dialog is modal-blocking, so cleanup goes through afterprint rather than a timer.
    const cleanup = () => {
        sheet.remove();
        window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);

    window.print();

    // Safari and some Linux builds never fire afterprint; this is the backstop so a stray
    // print sheet can never be left in the page.
    setTimeout(() => { if (sheet.isConnected) cleanup(); }, 60_000);

    return snap.rows.length;
}
